import React, { useEffect, useState } from 'react';
import '../../assets/styles/card.css';
import BASE_URL from "../../config";
import axios from 'axios';
import { MapContainer, TileLayer, Popup, Marker } from 'react-leaflet';
import RotatedMarker from './rotatedMarker';

function LocationTrackingCard() {
  const [device, setDevice] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [caseStatus, setCaseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('glass');
  const token = localStorage.getItem('jwt_token');

  useEffect(() => {
    const fetchDeviceAndStatus = async () => {
      try {
        const deviceResponse = await axios.post(
          `${BASE_URL}/api/get_cust_devices/`,
          {},
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const deviceData = Array.isArray(deviceResponse.data)
          ? deviceResponse.data[0]
          : deviceResponse.data;

        if (!deviceData || !deviceData.uid) {
          console.warn("No valid device data found.");
          setLoading(false);
          return;
        }

        setDevice(deviceData);

        // Fetch device location status
        const statusUrl = `${BASE_URL}/api/device-status-location/${deviceData.uid}/`;
        const statusResponse = await axios.get(statusUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const statusData = statusResponse.data;
        const latestStatus = Array.isArray(statusData)
          ? statusData.slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
          : statusData;

        if (latestStatus) {
          setDeviceStatus(latestStatus);
        } else {
          console.warn("No status records found.");
        }

        // Fetch case location status
        try {
          const caseStatusResponse = await axios.get(
            `${BASE_URL}/api/pocket/get-case-status/${deviceData.uid}/`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCaseStatus(caseStatusResponse.data);
        } catch (caseError) {
          console.warn("No case status data found:", caseError);
        }

      } catch (error) {
        console.error("Error fetching device or status:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDeviceAndStatus();
  }, [token]);

  const position = deviceStatus && deviceStatus.latitude && deviceStatus.longitude
    ? [deviceStatus.latitude, deviceStatus.longitude]
    : [0, 0];

  const casePosition = caseStatus && caseStatus.latitude && caseStatus.longitude
    ? [parseFloat(caseStatus.latitude), parseFloat(caseStatus.longitude)]
    : [0, 0];

  return (
    <article className="ltc-card" aria-labelledby="ltc-heading">
      <h2 id="ltc-heading" className="ltc-title">Last Seen Location</h2>

      <div className="ltc-toggle">
        <button
          className={`ltc-toggle-btn${selectedView === 'glass' ? ' active' : ''}`}
          onClick={() => setSelectedView('glass')}
          type="button"
        >
          Glass
        </button>
        <button
          className={`ltc-toggle-btn${selectedView === 'case' ? ' active' : ''}`}
          onClick={() => setSelectedView('case')}
          type="button"
        >
          Case
        </button>
      </div>

      {loading ? (
        <p className="ltc-message" role="status" aria-live="polite">Loading...</p>
      ) : selectedView === 'glass' ? (
        deviceStatus ? (
          <div className="ltc-map-wrapper" role="img" aria-label={`Map showing device location at latitude ${deviceStatus.latitude}, longitude ${deviceStatus.longitude}`}>
            <MapContainer center={position} zoom={13} key={`glass-${position[0]}-${position[1]}`}>
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {deviceStatus?.compass_heading !== undefined ? (
                <RotatedMarker
                  position={position}
                  rotationAngle={deviceStatus.compass_heading || 0}
                  popupText={
                    <Popup>
                      Device: {device?.serial}<br />
                      Latitude: {deviceStatus.latitude}<br />
                      Longitude: {deviceStatus.longitude}<br />
                      Heading: {deviceStatus.compass_heading}°
                    </Popup>
                  }
                />
              ) : (
                <Marker position={position}>
                  <Popup>
                    Device: {device?.serial}<br />
                    Latitude: {deviceStatus.latitude}<br />
                    Longitude: {deviceStatus.longitude}
                  </Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
        ) : (
          <p className="ltc-message" role="status">No location data found.</p>
        )
      ) : (
        caseStatus ? (
          <div className="ltc-map-wrapper" role="img" aria-label={`Map showing case location at latitude ${caseStatus.latitude}, longitude ${caseStatus.longitude}`}>
            <MapContainer center={casePosition} zoom={13} key={`case-${casePosition[0]}-${casePosition[1]}`}>
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={casePosition}>
                <Popup>
                  Case: {caseStatus.case_serial}<br />
                  Latitude: {caseStatus.latitude}<br />
                  Longitude: {caseStatus.longitude}
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        ) : (
          <p className="ltc-message" role="status">No case location data found.</p>
        )
      )}
    </article>
  );
}

export default LocationTrackingCard;
